import { useEffect, useMemo, useRef } from 'react';
import { makeStyles, tokens, Button } from '@fluentui/react-components';
import { ChevronLeft20Regular } from '@fluentui/react-icons';

export interface Crumb {
  label: string;
  onClick?: () => void; // tappable when present and not the last crumb
}

interface Props {
  items: Crumb[];
  onBack?: () => void;
}

const useStyles = makeStyles({
  bar: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.98)',
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: '#E7EDF4',
    paddingTop: '8px',
    paddingBottom: '8px',
    paddingLeft: '12px',
    paddingRight: '12px',
    gap: '8px',
    flexShrink: 0,
    minHeight: '50px',
    boxShadow: '0 8px 18px rgba(17,24,36,0.04)',
  },
  back: {
    minWidth: '38px',
    minHeight: '38px',
    flexShrink: 0,
    borderRadius: '999px',
    color: '#445061',
    backgroundColor: '#F7FAFD',
    boxShadow: 'inset 0 0 0 1px rgba(17,24,36,0.08)',
  },
  trail: {
    display: 'flex',
    alignItems: 'center',
    overflowX: 'auto',
    overflowY: 'hidden',
    WebkitOverflowScrolling: 'touch',
    flex: 1,
    gap: '7px',
    whiteSpace: 'nowrap',
    scrollbarWidth: 'none',
    scrollSnapType: 'x proximity',
    '::-webkit-scrollbar': { display: 'none' } as any,
  },
  item: {
    display: 'inline-flex',
    alignItems: 'center',
    flexShrink: 0,
    marginRight: '0',
    scrollSnapAlign: 'start',
  },
  crumb: {
    position: 'relative',
    fontSize: '11px',
    color: '#6F0F12',
    backgroundColor: '#FFF6F7',
    borderTopWidth: '1px',
    borderRightWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: '#F6C7CA',
    borderRightColor: '#F6C7CA',
    borderBottomColor: '#F6C7CA',
    borderLeftColor: '#F6C7CA',
    paddingTop: '8px',
    paddingBottom: '8px',
    paddingLeft: '12px',
    paddingRight: '12px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    borderRadius: '999px',
    appearance: 'none',
    minHeight: '34px',
    fontWeight: 600,
    letterSpacing: 0,
    ':active': { color: tokens.colorBrandForeground1 },
  },
  current: {
    position: 'relative',
    fontSize: '11px',
    fontWeight: 600,
    color: '#FFFFFF',
    background: 'linear-gradient(180deg, #F13A40, #E3272D)',
    borderRadius: '999px',
    paddingTop: '8px',
    paddingBottom: '8px',
    paddingLeft: '12px',
    paddingRight: '12px',
    minHeight: '34px',
    display: 'inline-flex',
    alignItems: 'center',
    boxShadow: '0 8px 16px rgba(227,39,45,0.18)',
    letterSpacing: 0,
  },
});

export function Breadcrumbs({ items, onBack }: Props) {
  const styles = useStyles();
  const currentRef = useRef<HTMLSpanElement | null>(null);
  const itemKey = useMemo(() => items.map(item => item.label).join('|'), [items]);

  useEffect(() => {
    window.requestAnimationFrame(() => {
      currentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    });
  }, [itemKey]);

  if (!items.length) return null;

  let backTarget: (() => void) | null = null;
  if (onBack) {
    backTarget = onBack;
  } else {
    for (let i = items.length - 2; i >= 0; i--) {
      if (items[i].onClick) {
        backTarget = items[i].onClick!;
        break;
      }
    }
  }

  return (
    <div className={styles.bar}>
      {backTarget && (
        <Button
          appearance="subtle"
          icon={<ChevronLeft20Regular />}
          className={styles.back}
          onClick={backTarget}
          aria-label="Back"
        />
      )}
      <nav className={styles.trail} aria-label="Breadcrumb">
        {items.map((c, i) => {
          const isLast = i === items.length - 1;
          return (
            <span key={`${i}-${c.label}`} className={styles.item}>
              {isLast || !c.onClick ? (
                <span ref={isLast ? currentRef : undefined} className={styles.current} aria-current={isLast ? 'page' : undefined}>
                  {c.label}
                </span>
              ) : (
                <button className={styles.crumb} onClick={c.onClick}>
                  {c.label}
                </button>
              )}
            </span>
          );
        })}
      </nav>
    </div>
  );
}
